import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';

type SelectTriggerProps = {
  'aria-label'?: string;
  /** Optional icon shown to the left of the label (e.g. SwapVert for sort controls). */
  icon?: React.ReactNode;
  /** Dimmed prefix label (e.g. "SBEF"). Omit for single-value triggers. */
  prefix?: string;
  /** Value label or node (e.g. "All", "Boverket", or rich text with subscripts). */
  value?: React.ReactNode;
  /**
   * String to compare against {@link value}. When they match the value is
   * rendered as a placeholder (30% opacity). Only has effect when {@link value}
   * is a plain string.
   */
  placeholder?: string;
  /** Optional content rendered between the value and the caret (e.g. a secondary icon+label pair). */
  suffix?: React.ReactNode;
  /** Extra class for the value label (e.g. to match another trigger’s weight). */
  valueClassName?: string;
  /** Hide the caret when the trigger is represented by an icon and optional badge. */
  showCaret?: boolean;
  open: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

/**
 * Compact dropdown trigger matching the {@link ElementListSortControl} style:
 * small rounded box, faded caret, hover tint. Use with a {@link Menu} for the panel.
 *
 * Renders a native button so it is focusable and opens on Enter/Space.
 */
export const SelectTrigger: React.FC<SelectTriggerProps> = ({
  icon,
  prefix,
  value,
  placeholder,
  suffix,
  valueClassName,
  showCaret = true,
  open,
  onClick,
  'aria-label': ariaLabel,
}) => {
  const { classes, cx } = useStyles();
  const isPlaceholder =
    typeof value === 'string' &&
    placeholder !== undefined &&
    value === placeholder;
  return (
    <button
      type="button"
      className={classes.root}
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={ariaLabel}
    >
      {icon && <Box className={classes.icon}>{icon}</Box>}
      {prefix && <Typography className={classes.prefix}>{prefix}</Typography>}
      {value !== undefined && (
        <Typography
          className={cx(
            classes.value,
            isPlaceholder && classes.valuePlaceholder,
            valueClassName,
          )}
        >
          {value}
        </Typography>
      )}
      {suffix && <Box className={classes.suffix}>{suffix}</Box>}
      {showCaret && (
        <Box className={classes.caret}>
          <Icon
            category="mui"
            icon={open ? 'KeyboardArrowUp' : 'KeyboardArrowDown'}
            size={12}
          />
        </Box>
      )}
    </button>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    height: 22,
    margin: 0,
    paddingBlock: 0,
    paddingInline: 6,
    border: 'none',
    borderRadius: 5,
    appearance: 'none',
    backgroundColor: 'transparent',
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
  },
  prefix: {
    fontSize: 10,
    fontWeight: 400,
    lineHeight: 1,
    color: 'rgba(0,0,0,0.5)',
    whiteSpace: 'nowrap',
  },
  value: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
    color: 'rgba(0,0,0,0.6)',
    whiteSpace: 'nowrap',
  },
  valuePlaceholder: {
    fontWeight: 500,
    color: 'rgba(0,0,0,0.4)',
    letterSpacing: '0.5px',
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
  },
  suffix: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    paddingLeft: 0,
  },
  caret: {
    opacity: 0.2,
    display: 'flex',
    alignItems: 'center',
  },
}));
